/**
 * Pure derivation of the Overview's delivery checklist.
 *
 * The agent writes a "What a Complete Delivery Looks Like" section into
 * `summary.md` as a *proposal* during onboarding, and revises it as the project
 * changes. The prompt requires each item be descriptive — "not a bare list" —
 * with the stated purpose setting its quality bar, so items are parsed as
 * label + purpose rather than as plain bullets.
 *
 * In practice the agent annotates items with its own status as the project moves
 * — a `- [x]` checkbox, a "Done — see cost.md" prefix, a trailing
 * "*Not started.*". Those markers are authoritative for completion and are
 * stripped from what the user sees, since the checkbox already conveys state and
 * a status phrase has no place in a request prompt.
 *
 * Only when no marker is present does completion fall back to matching the item
 * against the files that exist. That fallback is biased hard toward "not done": a
 * false open costs one redundant request, which TACO answers with "already done,
 * here it is", whereas a false done is active misinformation.
 */

import type {
  DeliveryDefinition,
  DeliveryDefinitionItem,
} from "@amzn/afxtaco-types";
import { DELIVERY_DEFINITION_VERSION } from "@amzn/afxtaco-types";

/** The heading the agent is instructed to use for this section. */
export const DELIVERY_SECTION_HEADING = "What a Complete Delivery Looks Like";

export interface DeliveryItem {
  /** Short label, e.g. "Cost model". */
  label: string;
  /** The purpose text that sets this item's quality bar, if the agent wrote one. */
  purpose?: string;
  /**
   * Whether this deliverable is complete. Taken from the agent's own marker when
   * it wrote one, and otherwise inferred from a backing file.
   */
  isDone: boolean;
}

/** A bullet line, capturing its content. */
const BULLET_LINE = /^\s*[-*+]\s+(.*)$/;

/** A markdown task checkbox at the start of a bullet's text. */
const TASK_CHECKBOX = /^\[([ xX])\]\s*/;

/**
 * The agent's own completion marker, which it writes in front of an item's text
 * once it has produced the deliverable — e.g. "Done — see cost.md".
 *
 * When present this is authoritative: the agent knows what it wrote, and it beats
 * guessing from filenames. Stripped from the displayed text so a checked row
 * doesn't also read "Done" next to its tick.
 */
// Alternation rather than a character class: these emoji carry variation
// selectors, which a class would silently mis-handle.
//
// A status word counts as a marker when it is the whole value ("Complete") or is
// immediately followed by a separator ("Done — see cost.md"). A leading tick is a
// marker on its own.
//
// What it must NOT match is a status word used as an ordinary adjective, where
// more prose follows with no separator: a deliverable *named* "Complete cost
// model", or a purpose reading "Completed auth flows and error handling." The
// original "starts with a status word" form matched both, which ticked the item
// wrongly and stripped that first word out of the text shown to the user and
// pasted into the chat box.
//
// One pattern for both positions. They differ only in that
// `splitLabelAndPurpose` has usually already consumed the separator by the time a
// label gets here, so labels tend to hit the whole-value branch and purposes the
// separator branch.
const DONE_MARKER = new RegExp(
  "^(?:" +
    // A tick is sufficient on its own, with or without a word after it.
    "(?:✅|✔️|☑️)\\s*(?:(?:done|complete|completed)\\b\\s*)?(?:[—–:-]\\s*)?" +
    "|" +
    // Bare word: either a separator follows, or it is the entire value.
    "(?:done|complete|completed)\\b\\s*(?:[—–:-]\\s*|\\.?\\s*$)" +
    ")",
  "i",
);

/**
 * Status the agent annotates an item with while tracking the delivery set —
 * "*Not started.*", "_In progress_", "(pending)".
 *
 * These describe the item's state, not the deliverable, so they belong in neither
 * the row (the checkbox already says it) nor a request prompt (asking TACO for
 * something "Not started." is nonsense). Matched only when emphasised,
 * parenthesised, or trailing, so a purpose legitimately containing the words
 * survives.
 */
const STATUS_WORDS =
  "not started|not yet started|in progress|in flight|pending|outstanding|todo|to do";

const EMPHASISED_STATUS = new RegExp(
  `[*_]{1,2}\\s*\\(?(?:${STATUS_WORDS})\\)?\\.?\\s*[*_]{1,2}`,
  "gi",
);

const TRAILING_STATUS = new RegExp(
  `[\\s—–-]*\\(?(?:${STATUS_WORDS})\\)?\\.?\\s*$`,
  "i",
);

/** Markdown emphasis markers, which read as noise in a chat prompt. */
const EMPHASIS_MARKERS = /[*_]{1,2}/g;

/**
 * Cleans an item's purpose for display and for use in a request.
 *
 * @returns the cleaned text, or undefined when nothing meaningful remains
 */
export function cleanPurpose(purpose: string | undefined): string | undefined {
  if (!purpose) return undefined;

  const cleaned = purpose
    .replace(EMPHASISED_STATUS, "")
    .replace(TRAILING_STATUS, "")
    .replace(DONE_MARKER, "")
    .replace(EMPHASIS_MARKERS, "")
    .replace(/\s+/g, " ")
    .trim()
    // Collapse any punctuation left dangling by the removals.
    .replace(/[\s,;:—–-]+$/, "");

  return cleaned || undefined;
}

/**
 * Composes the request for a deliverable the project doesn't have yet.
 *
 * Carries the quality bar the agent itself wrote, so the request states what the
 * artifact has to achieve rather than only naming it. Joined with an em dash
 * because the purpose is usually a noun phrase — "It should be: <fragment>" reads
 * like pasted documentation, which is what this used to be.
 */
export function buildDeliverableRequest(item: DeliveryItem): string {
  const purpose = cleanPurpose(item.purpose);
  if (!purpose) {
    return `Please produce the ${item.label} for this project.`;
  }

  const withStop = /[.!?]$/.test(purpose) ? purpose : `${purpose}.`;
  return `Please produce the ${item.label} for this project — ${withStop}`;
}

/**
 * Splits a bullet's text into a label and its purpose. The agent typically
 * writes "Label — purpose" (em dash), sometimes with a colon or hyphen.
 * Falls back to the whole line as the label when there's no separator.
 */
const splitLabelAndPurpose = (
  text: string,
): { label: string; purpose?: string } => {
  const stripped = text.replace(/\*\*/g, "").trim();
  const match = /^(.{2,80}?)\s*(?:—|–|:|\s-\s)\s*(.+)$/.exec(stripped);
  if (!match) return { label: stripped };
  return { label: match[1].trim(), purpose: match[2].trim() };
};

/**
 * Normalises a string into comparable word tokens, dropping filler that would
 * otherwise match everything.
 */
const STOP_WORDS = new Set([
  "the",
  "a",
  "an",
  "and",
  "or",
  "for",
  "of",
  "to",
  "with",
  "in",
  "on",
  "customer",
  "customers",
  "seller",
  "review",
  "complete",
  "full",
  "detailed",
]);

const tokenize = (text: string): Set<string> =>
  new Set(
    text
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .split(/\s+/)
      .filter((w) => w.length > 2 && !STOP_WORDS.has(w)),
  );

/**
 * Decides whether a delivery item is backed by one of the existing files.
 *
 * Requires a whole-token overlap between the item's label and a file's label —
 * substring matching would fire on far too much. Returns false whenever it isn't
 * sure, by design.
 */
const hasBackingFile = (
  itemLabel: string,
  existingLabels: readonly string[],
): boolean => {
  const itemTokens = tokenize(itemLabel);
  if (itemTokens.size === 0) return false;

  return existingLabels.some((fileLabel) => {
    const fileTokens = tokenize(fileLabel);
    if (fileTokens.size === 0) return false;
    // Every token of the shorter side must appear in the longer one, so
    // "Cost model" matches "Cost" but "Security boundary review" does not
    // match "Summary".
    const [smaller, larger] =
      itemTokens.size <= fileTokens.size
        ? [itemTokens, fileTokens]
        : [fileTokens, itemTokens];
    for (const token of smaller) {
      if (!larger.has(token)) return false;
    }
    return true;
  });
};

/** Longest plausible deliverable name, in words. Real ones are noun phrases. */
const MAX_LABEL_WORDS = 9;

/**
 * Phrases that mark a bullet as commentary about the delivery set rather than a
 * deliverable in it — the agent sometimes closes the section with a caveat like
 * "Deliverables will expand if this becomes grounded in a real engagement".
 *
 * Offering "Ask TACO for this" on a sentence like that is nonsense, so these are
 * dropped rather than rendered as items.
 */
const PROSE_SIGNALS =
  /\b(?:will |would |may |might |can be |should be |if this |once we |e\.?g\.?|depending on|as needed|to be determined|tbd)\b/i;

/**
 * Whether a parsed bullet looks like an actual deliverable.
 *
 * Deliverable names are short noun phrases ("Cost model", "Business value
 * assessment"). Commentary is a sentence: long, verby, often parenthetical. The
 * test is deliberately conservative about *rejecting* — a wrongly-kept caveat is
 * cosmetic noise, whereas a wrongly-dropped deliverable hides real work.
 */
const isPlausibleDeliverable = (label: string): boolean => {
  if (!label) return false;

  const words = label.split(/\s+/).filter(Boolean);
  if (words.length > MAX_LABEL_WORDS) return false;

  // A trailing period on a multi-word label reads as a sentence, not a name.
  if (words.length > 4 && /[.!?]$/.test(label)) return false;

  return !PROSE_SIGNALS.test(label);
};

/**
 * Parses the delivery section into items, marking each complete or outstanding.
 *
 * Completion comes from the agent's own marker when it wrote one (a `- [x]`
 * checkbox, or a "Done — see …" prefix), because the agent knows what it
 * produced. Only when there is no marker does this fall back to matching the
 * item against the files that exist — and that fallback stays biased toward
 * outstanding, since a wrong "done" is misinformation while a wrong "open" costs
 * one redundant request.
 *
 * @param sectionBody - the section extracted from `summary.md`, or undefined
 *   when the agent never wrote it
 * @param existingLabels - labels of deliverables that exist in S3
 * @returns the items, or an empty array when there's no section to parse
 */
export function buildDeliveryChecklist(
  sectionBody: string | undefined,
  existingLabels: readonly string[],
): DeliveryItem[] {
  if (!sectionBody) return [];

  const items: DeliveryItem[] = [];

  for (const line of sectionBody.split("\n")) {
    const match = BULLET_LINE.exec(line);
    if (!match) continue;

    let text = match[1].trim();
    if (!text) continue;

    // `- [x] Cost model` — the agent's task-checkbox convention.
    let markedDone = false;
    const checkbox = TASK_CHECKBOX.exec(text);
    if (checkbox) {
      markedDone = checkbox[1].toLowerCase() === "x";
      text = text.slice(checkbox[0].length).trim();
    }

    const { label, purpose } = splitLabelAndPurpose(text);
    if (!isPlausibleDeliverable(label)) continue;

    // "Done — see cost.md", written by the agent into the label or the purpose.
    if (DONE_MARKER.test(label) || (purpose && DONE_MARKER.test(purpose))) {
      markedDone = true;
    }

    // Strip status annotations and markdown noise for both the row and any
    // request built from it — the checkbox already conveys state.
    const cleanedPurpose = cleanPurpose(purpose);

    items.push({
      label: label.replace(DONE_MARKER, "").trim() || label,
      ...(cleanedPurpose ? { purpose: cleanedPurpose } : {}),
      isDone: markedDone || hasBackingFile(label, existingLabels),
    });
  }

  return items;
}

// ---------------------------------------------------------------------------
// Structured definition (preferred source)
// ---------------------------------------------------------------------------

/**
 * Reads the delivery definition the agent wrote with `set_delivery_definition`.
 *
 * Returns undefined for anything it does not fully trust: absent file, malformed
 * JSON, unrecognised schema version, or no usable items. The caller then falls
 * back to parsing the prose section, so a bad file degrades to today's behaviour
 * rather than emptying the card.
 *
 * @param json - raw contents of the delivery-definition asset
 * @returns the validated items, or undefined when the file can't be used
 */
export function parseDeliveryDefinition(
  json: string | undefined,
): DeliveryDefinitionItem[] | undefined {
  if (!json?.trim()) return undefined;

  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    // Not worth surfacing: the prose fallback covers it.
    return undefined;
  }

  const candidate = parsed as Partial<DeliveryDefinition> | null;
  if (!candidate || typeof candidate !== "object") return undefined;
  // An unknown version means a shape this build doesn't understand. Reading it
  // optimistically would render wrong data with full confidence.
  if (candidate.version !== DELIVERY_DEFINITION_VERSION) return undefined;
  if (!Array.isArray(candidate.items)) return undefined;

  // Every optional field is type-checked, not just `label`. This is untrusted
  // JSON from S3, and a non-string `purpose` used to survive the filter and then
  // throw inside `cleanPurpose` — which defeated the whole point of returning
  // undefined here, since the crash happened after the fallback decision.
  const items = candidate.items.filter(
    (item): item is DeliveryDefinitionItem => {
      if (!item || typeof item !== "object") return false;
      const candidateItem = item as DeliveryDefinitionItem;
      if (
        typeof candidateItem.label !== "string" ||
        candidateItem.label.trim().length === 0
      ) {
        return false;
      }
      // Dropped rather than coerced: an item whose fields are the wrong type came
      // from something that isn't writing this schema, so its values aren't
      // trustworthy either.
      if (
        candidateItem.purpose !== undefined &&
        typeof candidateItem.purpose !== "string"
      ) {
        return false;
      }
      if (
        candidateItem.path !== undefined &&
        typeof candidateItem.path !== "string"
      ) {
        return false;
      }
      return true;
    },
  );

  return items.length > 0 ? items : undefined;
}

/**
 * Turns the structured definition into checklist rows.
 *
 * Completion is still derived, never read from the file. The agent supplies the
 * item-to-file mapping, which is the part it actually knows and the part that was
 * previously guessed; whether the work exists is answered by the manifest. That
 * split is deliberate: the tool replaces the whole definition on every revision,
 * so any state stored in it would have to be re-stated correctly each time, and
 * one omission would silently un-complete work the user had already built.
 *
 * Items with no `path` fall back to the label matcher, so an agent that proposes a
 * deliverable before deciding where it lives still gets a sensible tick.
 *
 * @param items - validated items from {@link parseDeliveryDefinition}
 * @param existingPaths - project-relative paths of deliverables that exist
 * @param existingLabels - labels of those same deliverables, for the fallback
 */
export function buildChecklistFromDefinition(
  items: readonly DeliveryDefinitionItem[],
  existingPaths: readonly string[],
  existingLabels: readonly string[],
): DeliveryItem[] {
  const present = new Set(existingPaths);

  return items.map((item) => {
    const label = item.label.trim();
    const purpose = cleanPurpose(item.purpose);

    return {
      label,
      ...(purpose ? { purpose } : {}),
      isDone: item.path
        ? present.has(item.path)
        : hasBackingFile(label, existingLabels),
    };
  });
}

/**
 * The checklist for a project, from the best source available.
 *
 * Prefers the structured definition and falls back to parsing the prose section
 * out of `summary.md`. The fallback is not just for old projects: it also covers a
 * turn where the agent updated the prose but did not call the tool.
 */
export function buildChecklist({
  definitionJson,
  sectionBody,
  existingPaths,
  existingLabels,
}: {
  definitionJson: string | undefined;
  sectionBody: string | undefined;
  existingPaths: readonly string[];
  existingLabels: readonly string[];
}): DeliveryItem[] {
  const structured = parseDeliveryDefinition(definitionJson);
  if (structured) {
    return buildChecklistFromDefinition(
      structured,
      existingPaths,
      existingLabels,
    );
  }
  return buildDeliveryChecklist(sectionBody, existingLabels);
}
